import map from './map';

type Resolvable<R> = R | PromiseLike<R>;

type IterateFunction<T, R> = (item: T, index: number) => Resolvable<R>;

/**
 * Map an array (or any iterable) which contains a list of promises (or a mix of promises and values) with the given `mapper` function with the signature `(item, index)`
 * where `item` is the resolved value of a respective promise in the input array.
 * If any promise in the input array is rejected the returned promise is rejected as well.
 *
 * Unlike normal map, this will then flatten the resulting array.
 *
 * If the `mapper` function returns promises or thenables, the returned promise will wait for all the mapped results to be resolved as well.
 *
 * `options.concurrency` represents the maximum amount of promises that can run at a given time. This prevents OOM errors for very large lists of `values` by only allowing
 * `options.concurrency` concurrent `mapper` functions to execute simultaneously. The minimum allowed `options.concurrency` is 1
 *
 * *The original array is not modified.*
 *
 * This is a combo of the `Bluebird.map` function and this implementation: https://betterprogramming.pub/implement-your-own-bluebird-style-promise-map-in-js-7c081b7ad02c
 * and this implementation https://gist.github.com/Yopadd/d1381e0fdc1aa6bedaeb36b7a8381892.
 *
 */
export default async function flatMap<R, U>(
  values: Iterable<Resolvable<R>>,
  mapper: IterateFunction<R, U>,
  options: { concurrency: number },
): Promise<Array<Awaited<U extends ReadonlyArray<(infer InnerArr)> ? InnerArr : U>>> {
  const results = await map(values, mapper, { concurrency: options.concurrency });
  return Promise.all(results.flat().map((result) => Promise.resolve(result)));
}
